/* Vyvata Score Engine — deterministic v1.
 *
 * Single public entry point: vsfScore(input, catalog) → output.
 *
 * Designed so an AI implementation can be dropped in later without changing
 * the frontend. The contract is what stays stable; the implementation behind
 * it can evolve. Goal mapping, narrative templates, and matching logic are
 * isolated as named functions so any one of them can be replaced piecemeal.
 */

(function () {
  'use strict';

  // ---------- User-facing goals (8) → product goal_tags (one-to-many) ----------
  // The quiz UI shows the keys here; products are tagged with the values.
  const GOAL_TO_TAGS = {
    sleep_recovery:     ['sleep', 'recovery'],
    energy_vitality:    ['energy'],
    focus_cognition:    ['cognitive', 'mood'],
    reduce_inflammation:['inflammation'],
    longevity_healthspan:['longevity', 'cardiovascular', 'immune'],
    strength_muscle:    ['recovery', 'energy'],
    athletic_recovery:  ['recovery', 'inflammation'],
    weight_loss:        ['energy', 'gut', 'inflammation'],
  };

  const GOAL_DISPLAY = {
    sleep_recovery:     'Sleep & Recovery',
    energy_vitality:    'Energy & Vitality',
    focus_cognition:    'Focus & Cognition',
    reduce_inflammation:'Reduce Inflammation',
    longevity_healthspan:'Longevity & Healthspan',
    strength_muscle:    'Strength & Muscle',
    athletic_recovery:  'Athletic Recovery',
    weight_loss:        'Weight Loss',
  };

  // ---------- Tier thresholds (out of 100, normalized) ----------
  const TIER_THRESHOLDS = [
    { min: 80, label: 'EXCELLENT' },
    { min: 65, label: 'GOOD' },
    { min: 50, label: 'FAIR' },
    { min: 0,  label: 'NEEDS WORK' },
  ];

  // ---------- v2 Score weights — coverage / habits ----------
  // The quiz only captures subcategory ticks, not specific brands. We don't
  // know the quality of the user's actual stack, and the catalog isn't deep
  // enough yet for a brand-level proxy to be honest. So the score weighs
  // only two signals we can measure: goal coverage and lifestyle habits.
  // Sums to 1.0.
  const SCORE_WEIGHTS = {
    coverage: 0.50,  // % of user's goals where at least one ticked subcategory addresses the goal
    habits:   0.50,  // avg habit slider (1–10 → 10–100), neutral 50 if not provided
  };

  // ---------- Phase A: Symptom → subcategory map ----------
  // The quiz asks "what are you experiencing?" — symptoms map to subcategories
  // that address them. Used by computeMissing to surface relevant picks even
  // when the user didn't tick a goal that points at the same subcategory.
  // Subcategory keys must match assets/vsf-taxonomy.json.
  const SYMPTOM_TO_SUBCATEGORIES = {
    low_energy:    ['energy_metabolic', 'vitamins_micronutrients', 'cardiovascular_heart', 'protein_amino_acids'],
    brain_fog:     ['cognitive_nootropics', 'stress_mood_mental', 'longevity_cellular'],
    poor_sleep:    ['sleep_supplements_nutraceuticals', 'stress_mood_mental'],
    joint_pain:    ['joint_bone_connective', 'immune_antioxidants'],
    mood:          ['stress_mood_mental', 'cognitive_nootropics'],
    weight:        ['weight_metabolic_health', 'energy_metabolic', 'gut_digestive'],
    digestive:     ['gut_digestive', 'immune_antioxidants'],
    inflammation:  ['immune_antioxidants', 'joint_bone_connective', 'cardiovascular_heart'],
    hormonal:      ['hormonal_endocrine', 'womens_health', 'mens_health'],
    skin_hair:     ['skin_hair_beauty', 'vitamins_micronutrients'],
  };

  // Display labels for symptoms (used by the verdict to name what the user reported).
  const SYMPTOM_DISPLAY = {
    low_energy:   'low energy',
    brain_fog:    'brain fog',
    poor_sleep:   'poor sleep',
    joint_pain:   'joint pain',
    mood:         'mood',
    weight:       'weight',
    digestive:    'digestive issues',
    inflammation: 'inflammation',
    hormonal:     'hormonal shifts',
    skin_hair:    'skin or hair changes',
  };

  // ---------- v2 Goal → subcategory map ----------
  // Maps each of the 8 user-facing goals to the canonical subcategory keys that
  // *plausibly* support that goal. Source: subjective read of the v2 rubric.
  // Spans all 5 categories. Tweak after launch based on signal.
  const GOAL_TO_SUBCATEGORIES = {
    sleep_recovery: [
      'sleep_supplements_nutraceuticals', 'stress_mood_mental',
      'sleep_tracking_wearables', 'smart_mattresses', 'sleep_environment',
      'white_noise_sound', 'noncontact_sleep_monitors', 'sleep_pillows_positional',
      'percussion_vibration', 'hrv_recovery_apps',
    ],
    energy_vitality: [
      'energy_metabolic', 'vitamins_micronutrients', 'cardiovascular_heart',
      'longevity_cellular', 'hormonal_endocrine',
      'cgm', 'smart_rings', 'smartwatches', 'fitness_activity_trackers',
      'red_light_photobiomodulation', 'sauna_heat_therapy',
    ],
    focus_cognition: [
      'cognitive_nootropics', 'stress_mood_mental', 'longevity_cellular',
      'neurostim_sleep_headbands',
    ],
    reduce_inflammation: [
      'immune_antioxidants', 'cardiovascular_heart', 'gut_digestive',
      'red_light_photobiomodulation', 'sauna_heat_therapy', 'cold_therapy',
      'food_sensitivity_allergy',
    ],
    longevity_healthspan: [
      'longevity_cellular', 'cardiovascular_heart', 'vitamins_micronutrients',
      'protein_amino_acids', 'hormonal_endocrine',
      'red_light_photobiomodulation', 'sauna_heat_therapy', 'hbot',
      'cgm', 'smart_rings', 'cardiac_ecg',
      'blood_panel_metabolic', 'nutrient_deficiency_testing', 'genetic_dna',
      'cancer_chronic_screening',
    ],
    strength_muscle: [
      'protein_amino_acids', 'vitamins_micronutrients', 'hormonal_endocrine',
      'percussion_vibration', 'ems_tens', 'compression_therapy',
      'fitness_activity_trackers', 'smartwatches',
    ],
    athletic_recovery: [
      'protein_amino_acids', 'joint_bone_connective',
      'percussion_vibration', 'compression_therapy', 'cold_therapy',
      'contrast_therapy', 'red_light_photobiomodulation', 'ems_tens',
      'stretch_mobility', 'hrv_recovery_apps', 'smart_rings',
    ],
    weight_loss: [
      'weight_metabolic_health', 'gut_digestive', 'protein_amino_acids',
      'cgm', 'fitness_activity_trackers', 'smartwatches',
      'blood_panel_metabolic', 'glucose_diabetes_monitoring',
    ],
  };

  // The 5 canonical category keys, in display order.
  const CATEGORY_KEYS = [
    'supplements', 'wearables_tracking', 'diagnostics_testing',
    'sleep_optimization', 'recovery_regeneration',
  ];

  // ---------- Age bands → goal_tags worth flagging ----------
  const AGE_BANDS = [
    { min: 18, max: 29, tags: ['energy', 'recovery'] },
    { min: 30, max: 44, tags: ['energy', 'mood'] },
    { min: 45, max: 59, tags: ['longevity', 'cardiovascular', 'joint'] },
    { min: 60, max: 90, tags: ['longevity', 'cardiovascular', 'cognitive', 'inflammation', 'joint'] },
  ];

  function ageBandTags(age) {
    if (typeof age !== 'number') return [];
    const band = AGE_BANDS.find(b => age >= b.min && age <= b.max);
    return band ? band.tags : [];
  }

  // Unknown sex or age → permissive (no filter). Otherwise enforce the rules.
  function lifeStageAllowed(product, sex, age) {
    const ls = product && product.life_stage;
    if (!ls || ls === 'general') return true;
    if (sex == null || typeof age !== 'number') return true;
    switch (ls) {
      case 'prenatal':         return sex === 'female' && age >= 18 && age <= 45;
      case 'postmenopausal':   return sex === 'female' && age >= 45;
      case 'men_50_plus':      return sex === 'male'   && age >= 50;
      case 'women_50_plus':    return sex === 'female' && age >= 50;
      case 'adults_50_plus':   return age >= 50;
      case 'youth_under_18':   return age < 18;
      default:                 return true;
    }
  }

  // Phase 3 hook: cosine falloff multiplier clamped to [0.9, 1.1].
  // No-op (returns 1) when the product carries no age_relevance data.
  function ageRelevanceMultiplier(product, age) {
    const ar = product && product.age_relevance;
    if (!ar || typeof age !== 'number') return 1;
    const peak = ar.peak;
    const falloff = ar.falloff;
    if (typeof peak !== 'number' || typeof falloff !== 'number' || falloff <= 0) return 1;
    const dist = Math.abs(age - peak);
    if (dist >= falloff) return 1;
    const m = 1 + 0.1 * Math.cos((dist / falloff) * (Math.PI / 2));
    return Math.max(0.9, Math.min(1.1, m));
  }

  function joinProse(items) {
    if (!items || !items.length) return '';
    if (items.length === 1) return items[0];
    if (items.length === 2) return items[0] + ' & ' + items[1];
    return items.slice(0, -1).join(', ') + ' & ' + items[items.length - 1];
  }

  // Goal tags the triage regex false-positives on most often: "supports
  // restful sleep" mentioned as a side benefit can leave a prostate
  // supplement tagged only with ['sleep']. If a product has just one
  // goal_tag and it's one of these, require an actual ingredient signal
  // in the title before treating the tag as legitimate.
  const THIN_MATCH_NOISE_TAGS = ['sleep', 'recovery', 'energy'];
  const TAG_TITLE_SIGNALS = {
    sleep:    /magnesium|melatonin|theanine|glycine|valerian|gaba|chamomile|lavender|sleep|night|rest/i,
    recovery: /recovery|magnesium|amino|bcaa|electrolyte|post[\s-]?workout|muscle/i,
    energy:   /b[\s-]?12|coq10|iron|nad|ribose|caffeine|guarana|ginseng|cordyceps|energy|adaptogen/i,
  };
  function isThinMatch(product) {
    if (!product || !product.goal_tags || product.goal_tags.length !== 1) return false;
    const tag = product.goal_tags[0];
    if (THIN_MATCH_NOISE_TAGS.indexOf(tag) === -1) return false;
    const signal = TAG_TITLE_SIGNALS[tag];
    if (!signal) return false;
    return !signal.test(product.title || '');
  }

  // ============== Pure helper functions ==============

  // ---------- v2 subcategory helpers ----------
  // Products in the engine catalog carry vsf_subcategory as an array. The
  // Shopify metafield is `list.single_line_text_field` so in raw form it can
  // sometimes appear as a JSON-encoded string — handle both.
  function productSubcategories(p) {
    const raw = p && p.vsf_subcategory;
    if (!raw) return [];
    if (Array.isArray(raw)) return raw;
    if (typeof raw === 'string') {
      try {
        const parsed = JSON.parse(raw);
        return Array.isArray(parsed) ? parsed : [];
      } catch { return []; }
    }
    return [];
  }

  function productInSubcategory(p, subcategoryKey) {
    return productSubcategories(p).indexOf(subcategoryKey) !== -1;
  }

  function productInCategory(p, categoryKey) {
    return p && p.vsf_category === categoryKey;
  }

  // ---------- Ingredient extraction for diversity ----------
  function getIngredientFamily(title) {
    const lower = (title || '').toLowerCase();
    
    // Map products to ingredient families to prevent duplicates
    const families = [
      { family: 'magnesium', keywords: ['magnesium'] },
      { family: 'omega3', keywords: ['omega-3', 'omega 3', 'fish oil', 'krill oil', 'dha', 'epa'] },
      { family: 'vitamin_d', keywords: ['vitamin d'] },
      { family: 'vitamin_b12', keywords: ['b12', 'b-12', 'methylcobalamin', 'cyanocobalamin'] },
      { family: 'b_complex', keywords: ['b complex', 'b-complex'] },
      { family: 'vitamin_c', keywords: ['vitamin c', 'ascorbic'] },
      { family: 'multivitamin', keywords: ['multivitamin', 'multi vitamin', 'multi-vitamin'] },
      { family: 'probiotic', keywords: ['probiotic', 'lactobacillus', 'bifidobacterium'] },
      { family: 'turmeric', keywords: ['turmeric', 'curcumin'] },
      { family: 'ashwagandha', keywords: ['ashwagandha'] },
      { family: 'zinc', keywords: ['zinc'] },
      { family: 'iron', keywords: ['iron'] },
      { family: 'calcium', keywords: ['calcium'] },
      { family: 'melatonin', keywords: ['melatonin'] },
      { family: 'coq10', keywords: ['coq10', 'coenzyme q', 'ubiquinol'] },
      { family: 'protein', keywords: ['protein', 'whey', 'collagen'] },
      { family: 'creatine', keywords: ['creatine'] },
      { family: 'fiber', keywords: ['fiber', 'psyllium'] },
      { family: 'lions_mane', keywords: ['lion\'s mane', 'lions mane'] },
      { family: 'rhodiola', keywords: ['rhodiola'] },
      { family: 'ginseng', keywords: ['ginseng'] },
      { family: 'berberine', keywords: ['berberine'] },
      { family: 'nac', keywords: ['nac', 'n-acetyl', 'acetylcysteine'] },
      { family: 'resveratrol', keywords: ['resveratrol'] },
      { family: 'glucosamine', keywords: ['glucosamine', 'chondroitin', 'msm'] },
    ];
    
    for (const fam of families) {
      if (fam.keywords.some(kw => lower.indexOf(kw) !== -1)) {
        return fam.family;
      }
    }
    
    // Default: use first significant word of title as family
    const words = lower.split(/\s+/).filter(w => w.length > 3);
    return words[0] || 'unknown';
  }

  function extractIngredient(title) {
    const ingredients = {
      'magnesium': 'Magnesium',
      'omega-3': 'Omega-3',
      'omega 3': 'Omega-3',
      'fish oil': 'Fish oil',
      'vitamin d': 'Vitamin D',
      'b12': 'Vitamin B12',
      'probiot': 'Probiotics',
      'turmeric': 'Turmeric',
      'curcumin': 'Curcumin',
      'ashwagandha': 'Ashwagandha',
      'melatonin': 'Melatonin',
      'coq10': 'CoQ10',
      'creatine': 'Creatine',
      'collagen': 'Collagen',
      'zinc': 'Zinc',
      'iron': 'Iron',
      'beetroot': 'Beetroot',
      'beet': 'Beet root',
      'rhodiola': 'Rhodiola',
      'ginseng': 'Ginseng',
      'berberine': 'Berberine',
      'nac': 'NAC',
      'resveratrol': 'Resveratrol',
      'glucosamine': 'Glucosamine',
    };
    
    const lower = (title || '').toLowerCase();
    for (const [key, name] of Object.entries(ingredients)) {
      if (lower.indexOf(key) !== -1) return name;
    }
    return null;
  }

  // ---------- Ingredient matching (Phase A.2 — ingredient-level intake) ----------
  // Ingredient definitions live in assets/vsf-ingredients.json and are loaded
  // as window.VSF_INGREDIENTS by vsf-ingredients.js. Each ingredient has a key
  // (slug) and a list of aliases (substrings to match against product titles).
  function getIngredientDefs(subcategoryKey) {
    const tx = (typeof window !== 'undefined' && window.VSF_INGREDIENTS) || null;
    if (!tx || !tx.subcategories) return [];
    return tx.subcategories[subcategoryKey] || [];
  }

  function findIngredientDef(subcategoryKey, ingredientKey) {
    const defs = getIngredientDefs(subcategoryKey);
    for (const d of defs) {
      if (d.key === ingredientKey) return d;
    }
    return null;
  }

  // Does this product's title contain any of the given ingredient's aliases?
  function productHasIngredient(product, ingredientDef) {
    if (!product || !ingredientDef || !Array.isArray(ingredientDef.aliases)) return false;
    const lower = (product.title || '').toLowerCase();
    for (const alias of ingredientDef.aliases) {
      if (alias && lower.indexOf(alias.toLowerCase()) !== -1) return true;
    }
    return false;
  }

  // Normalize ingredient_ticks input shape from quiz: { subcatKey: [ingredientKey, ...] }
  function normalizeIngredientTicks(raw) {
    const out = {};
    if (!raw || typeof raw !== 'object') return out;
    for (const subKey of Object.keys(raw)) {
      const arr = raw[subKey];
      if (Array.isArray(arr) && arr.length) {
        out[subKey] = arr.filter(s => typeof s === 'string' && s.length);
      }
    }
    return out;
  }

  // ---------- v2: Stack matching (subcategory-tick based) ----------
  // For each ticked subcategory, returns the catalog top-scored product. If
  // the user also ticked specific ingredients in that subcategory (Phase A.2),
  // the match is narrowed to products containing one of those ingredients —
  // so "what's working" surfaces the highest-scored product that contains
  // something the user actually takes.
  function matchStack(subcategoryTicks, ingredientTicks, products, sexFilter, age) {
    if (!Array.isArray(subcategoryTicks) || subcategoryTicks.length === 0) return [];
    const picks = ingredientTicks || {};
    return subcategoryTicks.map(subcatKey => {
      const userIngredients = (picks[subcatKey] || [])
        .map(ik => findIngredientDef(subcatKey, ik))
        .filter(Boolean);

      let candidates = products.filter(p => {
        if (!productInSubcategory(p, subcatKey)) return false;
        if (sexFilter && p.sex_relevance && p.sex_relevance !== 'unisex' && p.sex_relevance !== 'neutral' && p.sex_relevance !== sexFilter) return false;
        if (!lifeStageAllowed(p, sexFilter, age)) return false;
        return true;
      });

      // If user ticked specific ingredients, narrow to products containing them.
      // If none match (catalog is shallow for that ingredient), fall back to
      // the broader subcategory candidates so the user still sees a reference.
      let narrowed = candidates;
      if (userIngredients.length > 0) {
        const filtered = candidates.filter(p =>
          userIngredients.some(ing => productHasIngredient(p, ing))
        );
        if (filtered.length > 0) narrowed = filtered;
      }

      if (narrowed.length === 0) return { subcategoryKey: subcatKey, product: null, userIngredients: userIngredients };
      narrowed.sort((a, b) => (b.score || 0) - (a.score || 0));
      return {
        subcategoryKey: subcatKey,
        product: narrowed[0],
        userIngredients: userIngredients, // [{ key, label, aliases }, ...]
      };
    });
  }

  // Collect the set of every ingredient the user has ticked, across all
  // subcategories — used to skip "missing" picks that just repeat what the
  // user already takes (e.g. don't push more magnesium when they already
  // ticked it under Vitamins).
  function collectUserIngredients(ingredientTicks) {
    const defs = [];
    if (!ingredientTicks) return defs;
    for (const subKey of Object.keys(ingredientTicks)) {
      for (const ik of ingredientTicks[subKey]) {
        const d = findIngredientDef(subKey, ik);
        if (d) defs.push(d);
      }
    }
    return defs;
  }

  // ---------- v2: Missing recommendations ----------
  // Top product per subcategory the user did NOT tick AND that's goal-relevant
  // OR symptom-relevant (Phase A). Phase A.2: filters out products whose main
  // ingredient is something the user already ticked elsewhere. Guarantees the
  // collagen-on-missing bug from the PDF cannot happen — if the user ticked
  // joint_bone_connective, joint_bone_connective is excluded here.
  function computeMissing(goals, symptoms, ticked, ingredientTicks, products, sexFilter, age) {
    const userIngredients = collectUserIngredients(ingredientTicks);
    // Subcategories the user's goals touch
    const relevantSubs = new Set();
    for (const g of goals) {
      const subs = GOAL_TO_SUBCATEGORIES[g] || [];
      for (const s of subs) relevantSubs.add(s);
    }
    // Phase A: subcategories the user's symptoms touch
    for (const sym of (symptoms || [])) {
      const subs = SYMPTOM_TO_SUBCATEGORIES[sym] || [];
      for (const s of subs) relevantSubs.add(s);
    }
    // Filter to ones the user has NOT ticked. Use Array.from for ordering.
    const missingSubs = Array.from(relevantSubs).filter(s => !ticked.has(s));

    const out = [];
    const usedFamilies = new Set();
    const usedHandles = new Set();

    for (const subKey of missingSubs) {
      const candidates = products.filter(p => {
        if (!productInSubcategory(p, subKey)) return false;
        if (sexFilter && p.sex_relevance && p.sex_relevance !== 'unisex' && p.sex_relevance !== 'neutral' && p.sex_relevance !== sexFilter) return false;
        if (!lifeStageAllowed(p, sexFilter, age)) return false;
        if (isThinMatch(p)) return false;
        // Phase A.2: don't push products whose main ingredient is something
        // the user already takes (in any subcategory).
        for (const userIng of userIngredients) {
          if (productHasIngredient(p, userIng)) return false;
        }
        return true;
      });
      if (!candidates.length) continue;
      candidates.sort((a, b) => (b.score || 0) - (a.score || 0));
      const top = candidates[0];
      // Diversity: one product per ingredient family, no dupes by handle
      const family = getIngredientFamily(top.title);
      if (usedFamilies.has(family)) continue;
      if (usedHandles.has(top.handle)) continue;
      usedFamilies.add(family);
      usedHandles.add(top.handle);
      // Which user-goal does this subcategory primarily address? Pick the first
      // goal whose mapping includes subKey.
      let goalReason = null;
      for (const g of goals) {
        if ((GOAL_TO_SUBCATEGORIES[g] || []).indexOf(subKey) !== -1) {
          goalReason = g; break;
        }
      }
      // Phase A: if no goal matched, this subcategory was surfaced because of
      // a user-reported symptom. Track which symptom drove it so the card
      // reason and verdict can name it specifically.
      let symptomReason = null;
      if (!goalReason) {
        for (const sym of (symptoms || [])) {
          if ((SYMPTOM_TO_SUBCATEGORIES[sym] || []).indexOf(subKey) !== -1) {
            symptomReason = sym; break;
          }
        }
      }
      out.push({ subcategoryKey: subKey, product: top, goalReason: goalReason, symptomReason: symptomReason });
      if (out.length >= 6) break;
    }
    return out;
  }

  // ---------- Phase A.2: Ingredient gaps inside ticked subcategories ----------
  // When user ticked a subcategory but only some of its ingredients, surface
  // the ingredients they DIDN'T tick (that exist in our catalog) as targeted
  // additions. Only for subcategories tied to a user goal or symptom.
  // Returns at most maxOut items, deduped against userIngredients globally.
  function computeMissingIngredients(goals, symptoms, ticked, ingredientTicks, products, sexFilter, age, maxOut) {
    const out = [];
    const userIngredients = collectUserIngredients(ingredientTicks);
    const userIngredientKeys = new Set(userIngredients.map(d => d.key));
    const max = maxOut || 4;

    for (const subKey of ticked) {
      // Is this subcategory relevant to a goal or symptom?
      let goalReason = null, symptomReason = null;
      for (const g of goals) {
        if ((GOAL_TO_SUBCATEGORIES[g] || []).indexOf(subKey) !== -1) { goalReason = g; break; }
      }
      if (!goalReason) {
        for (const sym of (symptoms || [])) {
          if ((SYMPTOM_TO_SUBCATEGORIES[sym] || []).indexOf(subKey) !== -1) { symptomReason = sym; break; }
        }
      }
      if (!goalReason && !symptomReason) continue;

      const userPicks = new Set((ingredientTicks && ingredientTicks[subKey]) || []);
      const defs = getIngredientDefs(subKey);
      if (!defs.length) continue;

      for (const def of defs) {
        if (userPicks.has(def.key)) continue;          // user already takes this
        if (userIngredientKeys.has(def.key)) continue;  // covered elsewhere

        const candidates = products.filter(p => {
          if (!productInSubcategory(p, subKey)) return false;
          if (sexFilter && p.sex_relevance && p.sex_relevance !== 'unisex' && p.sex_relevance !== 'neutral' && p.sex_relevance !== sexFilter) return false;
          if (!lifeStageAllowed(p, sexFilter, age)) return false;
          if (isThinMatch(p)) return false;
          return productHasIngredient(p, def);
        });
        if (!candidates.length) continue;
        candidates.sort((a, b) => (b.score || 0) - (a.score || 0));
        out.push({
          subcategoryKey: subKey,
          ingredientDef: def,
          product: candidates[0],
          goalReason: goalReason,
          symptomReason: symptomReason,
        });
        if (out.length >= max) return out;
      }
    }
    return out;
  }

  // ---------- v2: What else to consider (non-supplement categories) ----------
  // For each of the 4 non-supplement categories, return up to 4 top products
  // that are relevant to the user's goals. Used by the results page's
  // collapsible "What else to consider" section.
  function computeWhatElseToConsider(goals, products, sexFilter, age) {
    // Goal-relevant subcategory set, but only for non-supplement categories
    const relevantSubs = new Set();
    for (const g of goals) {
      for (const s of (GOAL_TO_SUBCATEGORIES[g] || [])) relevantSubs.add(s);
    }
    const out = {};
    for (const catKey of CATEGORY_KEYS) {
      if (catKey === 'supplements') continue;
      const candidates = products.filter(p => {
        if (!productInCategory(p, catKey)) return false;
        if (sexFilter && p.sex_relevance && p.sex_relevance !== 'unisex' && p.sex_relevance !== 'neutral' && p.sex_relevance !== sexFilter) return false;
        if (!lifeStageAllowed(p, sexFilter, age)) return false;
        // Prefer subcategories relevant to the user's goals; if none match,
        // fall through to the highest-scored items in the category.
        return true;
      });
      if (!candidates.length) { out[catKey] = []; continue; }
      // Two passes: goal-relevant first, then everything else as fill.
      candidates.sort((a, b) => (b.score || 0) - (a.score || 0));
      const goalRelevant = candidates.filter(p => productSubcategories(p).some(s => relevantSubs.has(s)));
      const fill = candidates.filter(p => !productSubcategories(p).some(s => relevantSubs.has(s)));
      out[catKey] = [...goalRelevant, ...fill].slice(0, 4);
    }
    return out;
  }

  // ---------- v2 Score calculation ----------
  // Two components, weighted per SCORE_WEIGHTS:
  //   1. Coverage (50%) — fraction of user's goals where at least one ticked
  //      subcategory addresses the goal. If user picked Sleep & Recovery and
  //      ticked any subcategory in GOAL_TO_SUBCATEGORIES['sleep_recovery'],
  //      that goal is covered.
  //   2. Habits (50%) — average habit slider scaled 1–10 → 10–100. Neutral 50
  //      if the user skipped habits entirely.
  //
  // "Stack quality" was removed (2026-05-24): the quiz captures category ticks,
  // not brands, and the catalog is too thin to honestly proxy stack quality
  // off the catalog top. Scoring now reflects only what we can measure.
  function calculateScore(matched, input) {
    const goals = input.goals || [];
    const ticked = new Set(input.subcategory_ticks || []);

    // 1) Coverage
    let coveragePct;
    if (goals.length === 0) {
      coveragePct = 50; // neutral — user didn't pick goals
    } else {
      let coveredGoals = 0;
      for (const g of goals) {
        const subs = GOAL_TO_SUBCATEGORIES[g] || [];
        if (subs.some(s => ticked.has(s))) coveredGoals++;
      }
      coveragePct = (coveredGoals / goals.length) * 100;
    }

    // 2) Habits
    let habitsPct = 50;
    if (input.habits) {
      const habitKeys = ['exercise', 'diet', 'sleep'];
      const values = habitKeys
        .map(k => input.habits[k])
        .filter(v => typeof v === 'number');
      if (values.length > 0) {
        const avgHabit = values.reduce((a, b) => a + b, 0) / values.length;
        habitsPct = avgHabit * 10; // 1–10 → 10–100
      }
    }

    const matchedWithProducts = matched.filter(m => m.product);
    const total =
      SCORE_WEIGHTS.coverage * coveragePct +
      SCORE_WEIGHTS.habits   * habitsPct;

    return {
      total: Math.max(0, Math.min(100, Math.round(total))),
      components: {
        coveragePct: Math.round(coveragePct),
        habitsPct: Math.round(habitsPct),
      },
      n_ticked: ticked.size,
      n_matched: matchedWithProducts.length,
    };
  }

  function scoreToTier(score) {
    for (const t of TIER_THRESHOLDS) if (score >= t.min) return t.label;
    return 'NEEDS WORK';
  }

  // ---------- Verdict generation (template-based, AI swap point) ----------
  // pluralize(1, 'item', 'items') === 'item'; pluralize(2, 'item') === 'items'
  function pluralize(n, sing, plur) {
    return n === 1 ? sing : (plur || sing + 's');
  }
  function isAre(n) { return n === 1 ? 'is' : 'are'; }
  // Short-form product title for in-sentence reference.
  function shortTitle(title) {
    const t = (title || '').trim();
    return t.length > 50 ? t.slice(0, 47) + '…' : t;
  }
  // v2: spans all 5 categories, no "wasting" / "weak link" language.
  // Composes positive prose around what's covered, what's missing, and what
  // non-supplement categories the user might want to add.
  // ---------- Phase A.3: actionable score-improvement tips ----------
  // Computes 2–4 concrete moves the user could make to push their score up,
  // each with the approximate point uplift. Surfaced in the "How to improve
  // your score" section on the results page.
  function generateTips(scoreData, input, missing) {
    const tips = [];
    const components = scoreData.components || {};
    const cov = components.coveragePct || 0;
    const hab = components.habitsPct || 0;
    const goals = input.goals || [];
    const ticked = new Set(input.subcategory_ticks || []);

    // Tip 1 — close coverage gap. Find first goal not yet covered.
    if (cov < 100 && goals.length > 0) {
      for (const g of goals) {
        const subs = GOAL_TO_SUBCATEGORIES[g] || [];
        const tickedForGoal = subs.some(s => ticked.has(s));
        if (!tickedForGoal && subs.length > 0) {
          const uplift = Math.round((1 / goals.length) * 100 * 0.5);
          tips.push({
            uplift: '+' + uplift + ' pts',
            text: `Tick any subcategory under ${GOAL_DISPLAY[g] || g} on Step 4 — even one will close this goal's coverage gap.`,
          });
          break;
        }
      }
    }

    // Tip 2 — habits sliders below 7
    if (input.habits && typeof input.habits === 'object') {
      const weakAreas = [];
      ['exercise', 'diet', 'sleep'].forEach(k => {
        const v = input.habits[k];
        if (typeof v === 'number' && v < 7) weakAreas.push({ key: k, value: v });
      });
      if (weakAreas.length > 0) {
        const weakest = weakAreas.sort((a, b) => a.value - b.value)[0];
        const target = 8;
        const delta = target - weakest.value;
        const uplift = Math.round(delta * 10 * 0.5 / 3); // 1 point of habit ~= 0.5/3 of total
        const labelMap = {
          exercise: 'Move exercise from ' + weakest.value + ' to 8 — daily walks, 3x/week strength baseline',
          diet:     'Move diet from ' + weakest.value + ' to 8 — whole foods, protein per meal, hit the protein floor',
          sleep:    'Move sleep from ' + weakest.value + ' to 8 — consistent bedtime, 7–8 hours, dark cool room',
        };
        tips.push({
          uplift: '+' + uplift + ' pts',
          text: labelMap[weakest.key] || `Move ${weakest.key} from ${weakest.value} to 8.`,
        });
      }
    } else if (hab === 50) {
      // User skipped habits entirely — neutral 50. Filling it in would shift the score.
      tips.push({
        uplift: '+10 to +20 pts',
        text: `Fill in the Lifestyle step — habits count for 50% of your score. If your exercise/diet/sleep are above 6, you're leaving real points on the table.`,
      });
    }

    // Tip 3 — symptom-driven specific ingredient
    if (missing && missing.length > 0) {
      const ingredientGap = missing.find(m => m.ingredient_reason && m.ingredient_reason.label);
      if (ingredientGap) {
        tips.push({
          uplift: 'specific',
          text: `Add ${ingredientGap.ingredient_reason.label} — you ticked this category but not this ingredient, and our top-scored option is ${shortTitle(ingredientGap.title)}.`,
        });
      }
    }

    // Tip 4 — bloodwork prompt if missing
    if (input.bloodwork_recent === false) {
      tips.push({
        uplift: 'baseline',
        text: `Get bloodwork — every other recommendation gets sharper once we know your actual vitamin D, ferritin, lipid, and inflammatory markers.`,
      });
    }

    return tips;
  }

  function generateVerdict(scoreData, matched, input, missing, whatElse) {
    const goalNames = (input.goals || []).map(g => GOAL_DISPLAY[g] || g);
    const symptomLabels = (input.symptoms || [])
      .map(s => SYMPTOM_DISPLAY[s])
      .filter(Boolean);
    const sex = input.sex;
    const age = input.age;
    const tier = scoreToTier(scoreData.total);

    const goalPhrase = goalNames.length === 0
      ? ''
      : goalNames.length === 1
        ? `Your priority: ${goalNames[0]}.`
        : goalNames.length === 2
          ? `Your priorities: ${goalNames[0]} and ${goalNames[1]}.`
          : `Your priorities: ${goalNames.slice(0, -1).join(', ')}, and ${goalNames[goalNames.length - 1]}.`;

    // Phase A: surface the symptoms the user reported so they feel heard.
    const concernsPhrase = symptomLabels.length === 0
      ? ''
      : `You mentioned dealing with ${joinProse(symptomLabels)} — we've prioritized recommendations that address ${symptomLabels.length === 1 ? 'this' : 'these'}.`;

    const bandTags = ageBandTags(age);
    const agePhrase = bandTags.length
      ? `Based on your age, we're also flagging ${joinProse(bandTags)} as areas worth optimizing.`
      : '';

    // Opening anchored by tier + the actual coverage signal (not wasting-count)
    let opening;
    const covered = scoreData.components ? scoreData.components.coveragePct : 0;
    const ticks = scoreData.n_ticked || 0;
    if (scoreData.n_matched === 0 && ticks === 0) {
      opening = `Clean slate. You haven't ticked any supplement categories yet — perfect time to build a foundation right the first time.`;
    } else if (tier === 'EXCELLENT') {
      opening = `Outstanding. You score ${scoreData.total}/100 — your coverage and habits are firing on all cylinders.`;
    } else if (tier === 'GOOD') {
      opening = `Solid foundation. ${scoreData.total}/100 — you've got good coverage across your goals with room to sharpen a few areas.`;
    } else if (tier === 'FAIR') {
      opening = `Decent baseline at ${scoreData.total}/100. ${ticks} ${pluralize(ticks, 'category')} ticked, ${covered}% of your goals covered. A few additions could move this materially.`;
    } else {
      opening = `${scoreData.total}/100 means there's significant room to upgrade. ${covered}% of your goals are currently covered by the categories you ticked. Let's close the gaps.`;
    }

    // Always name one top pick from missing so the user has at least one
    // specific actionable item without scrolling.
    let pickPhrase = '';
    if (missing && missing.length > 0) {
      const top = missing[0];
      pickPhrase = `Top pick to add: ${shortTitle(top.title)} (VSF ${top.vsf_score}).`;
    }

    // Mention non-supplement categories if any have goal-relevant picks
    let nonSuppPhrase = '';
    if (whatElse) {
      const nonEmpty = [];
      for (const catKey of CATEGORY_KEYS) {
        if (catKey === 'supplements') continue;
        if (whatElse[catKey] && whatElse[catKey].length) nonEmpty.push(catKey);
      }
      if (nonEmpty.length) {
        const labels = nonEmpty.map(k => ({
          wearables_tracking: 'wearables',
          diagnostics_testing: 'diagnostics',
          sleep_optimization: 'sleep optimization',
          recovery_regeneration: 'recovery tools',
        }[k] || k));
        nonSuppPhrase = `Beyond supplements, ${joinProse(labels)} could move the needle on your goals — see "What else to consider" below.`;
      }
    }

    // Personalized context
    const contextPhrases = [];
    if (typeof age === 'number' && age >= 50) {
      contextPhrases.push(`At 50+, supplement quality and absorption become even more critical — we've prioritized bioavailable forms.`);
    }
    if (sex === 'female' && goalNames.some(g => g.includes('Energy') || g.includes('Sleep'))) {
      contextPhrases.push(`For women optimizing energy and recovery, we're factoring in hormonal considerations and iron status.`);
    }
    if (sex === 'male' && goalNames.some(g => g.includes('Strength') || g.includes('Muscle'))) {
      contextPhrases.push(`For muscle and performance goals, we've emphasized protein quality, creatine efficacy, and testosterone support.`);
    }
    if (goalNames.some(g => g.includes('Longevity'))) {
      contextPhrases.push(`Longevity isn't just supplements — we're including lifestyle and biohacking protocols proven to extend healthspan.`);
    }

    // Phase A.3 — Diet pattern context
    const dietContext = {
      vegan:         `As a vegan, we're emphasizing B12, iron, omega-3 (algae-sourced), zinc, and creatine — common gaps on plant-only diets.`,
      vegetarian:    `As a vegetarian, watch B12, iron, zinc, and omega-3 — animal-sourced nutrients that need extra attention.`,
      keto:          `On a keto pattern, electrolytes (sodium, magnesium, potassium) and MCT support are featured.`,
      mediterranean: `Your Mediterranean pattern is one of the most evidence-backed bases — we're layering in targeted gaps rather than rebuilding the foundation.`,
      carnivore:     `On a carnivore pattern, vitamin C, fiber-driven gut support, and certain plant phytochemicals are common gaps to consider.`,
      other:         '',
      omnivore:      '',
    };
    if (input.diet_pattern && dietContext[input.diet_pattern]) {
      contextPhrases.push(dietContext[input.diet_pattern]);
    }

    // Phase A.3 — Alcohol context
    if (input.alcohol === 'regular' || input.alcohol === 'daily') {
      contextPhrases.push(`Regular alcohol use is in the picture — milk thistle, NAC, and B-complex are worth a look for liver support.`);
    }

    // Phase A.3 — Stress level (slider 1-10 in habits, if provided)
    const stressLevel = input.habits && typeof input.habits.stress === 'number' ? input.habits.stress : null;
    if (stressLevel !== null && stressLevel >= 7) {
      contextPhrases.push(`Stress at ${stressLevel}/10 is significant — adaptogens (ashwagandha, rhodiola) and cortisol-managing ingredients (phosphatidylserine, magnesium glycinate) are featured early in your recommendations.`);
    }

    // Phase A.3 — Bloodwork pointer
    if (input.bloodwork_recent === false) {
      contextPhrases.push(`You haven't had bloodwork in the last 12 months — the strongest first move is a baseline panel. We'll surface diagnostic options in "What else to consider."`);
    }

    // Phase A.3 — Summarize what the user's already taking (ingredient-level)
    if (input.ingredient_ticks && typeof input.ingredient_ticks === 'object') {
      const allIngredientLabels = [];
      for (const subKey of Object.keys(input.ingredient_ticks)) {
        for (const ingKey of (input.ingredient_ticks[subKey] || [])) {
          const def = findIngredientDef(subKey, ingKey);
          if (def) allIngredientLabels.push(def.label);
        }
      }
      if (allIngredientLabels.length > 0) {
        const list = allIngredientLabels.length <= 4
          ? joinProse(allIngredientLabels)
          : joinProse(allIngredientLabels.slice(0, 3)) + ` and ${allIngredientLabels.length - 3} more`;
        contextPhrases.push(`On your stack we recorded: ${list} — solid baseline to build on.`);
      }
    }

    const contextPhrase = contextPhrases.length > 0 ? contextPhrases.join(' ') : '';

    return [opening, goalPhrase, concernsPhrase, agePhrase, pickPhrase, nonSuppPhrase, contextPhrase].filter(Boolean).join(' ');
  }

  // ---------- Reason generation per product (varied) ----------
  // Phase A.2: handles four "why-we-surfaced-this" sources, in priority:
  //   - ingredientDef: surfaced because user is missing a specific ingredient
  //     (e.g. "you ticked Vitamins but didn't pick Magnesium")
  //   - goal: user's quiz goal (e.g. "Sleep & Recovery")
  //   - symptom: user-reported concern (e.g. "low energy")
  //   - neither: generic "your concerns" — defensive fallback
  function reasonForRecommendation(product, goal, index, symptom, ingredientDef) {
    // Ingredient-gap explanation wins when present — it's the most specific.
    if (ingredientDef && ingredientDef.label) {
      const score = product.score || 0;
      return `Top-scored ${ingredientDef.label} pick in our catalog (VSF ${score})`;
    }
    const goalLabel = goal ? (GOAL_DISPLAY[goal] || goal) : null;
    const symptomLabel = symptom ? (SYMPTOM_DISPLAY[symptom] || symptom) : null;
    const forPhrase = goalLabel || (symptomLabel ? `your ${symptomLabel} concern` : 'your concerns');
    const forPhraseLower = forPhrase.toLowerCase();
    const score = product.score || 0;
    const certs = product.certifications || [];
    const ingredient = extractIngredient(product.title || '');

    // Vary based on index to ensure diversity in language
    const variety = (index || 0) % 5;

    if (score >= 80) {
      if (variety === 0) return `Top-rated for ${forPhrase} (VSF ${score})`;
      if (variety === 1) return `Highest-scored option for ${forPhrase}`;
      if (variety === 2 && certs.length) return `${certs[0]} certified, excellent for ${forPhrase}`;
      if (variety === 3 && ingredient) return `${ingredient} excels at ${forPhraseLower}`;
      return `Elite choice for ${forPhrase} (VSF ${score})`;
    }

    if (score >= 70) {
      if (variety === 0 && ingredient) return `${ingredient} supports ${forPhraseLower}`;
      if (variety === 1) return `Strong evidence for ${forPhrase} (VSF ${score})`;
      if (variety === 2 && certs.length) return `${certs[0]} certified for ${forPhrase}`;
      if (variety === 3) return `Reliable option for ${forPhrase}`;
      return `Well-researched for ${forPhrase}`;
    }

    if (score >= 60) {
      if (variety === 0 && ingredient) return `${ingredient} promotes ${forPhraseLower}`;
      if (variety === 1) return `Good choice for ${forPhrase}`;
      if (variety === 2 && certs.length) return `${certs[0]} for ${forPhrase}`;
      if (variety === 3) return `Solid evidence for ${forPhrase}`;
      return `Quality option for ${forPhrase}`;
    }

    // Default fallback
    if (certs.length && variety < 2) return `${certs[0]} for ${forPhrase}`;
    if (ingredient) return `${ingredient} enhances ${forPhraseLower}`;
    return `Supports ${forPhraseLower}`;
  }

  // v2: simpler — matched[i] is { subcategoryKey, product }, no preset/proxy logic
  function reasonForCurrent(matched) {
    if (!matched.product) return `No product in our catalog yet for this subcategory.`;
    const score = matched.product.score || 0;
    if (score >= 70) return `Top-scored in this subcategory (VSF ${score}/100).`;
    if (score >= 50) return `Best available in this subcategory (VSF ${score}/100).`;
    return `Best available in this subcategory (VSF ${score}/100). Worth comparing alternatives.`;
  }

  // ---------- Phase B: relevance tags (chips shown on each card) ----------
  // Surfaces engine signals the user otherwise can't see — life_stage matches,
  // sex_relevance matches, classification confidence — so each card explains
  // why this product is or isn't a fit for their demographic.
  function relevanceTagsFor(product, userInput) {
    const tags = [];
    if (!product) return tags;
    const userAge = (typeof userInput.age === 'number') ? userInput.age : null;
    const userSex = userInput.sex || null;

    const ls = product.life_stage;
    const sr = product.sex_relevance;

    // Life-stage matches (positive when user demographic aligns)
    if (ls === 'adults_50_plus' && userAge !== null && userAge >= 50) {
      tags.push({ key: 'demo-match', label: 'For adults 50+', match: true });
    } else if (ls === 'men_50_plus' && userAge !== null && userAge >= 50 && userSex === 'male') {
      tags.push({ key: 'demo-match', label: 'For men 50+', match: true });
    } else if (ls === 'postmenopausal' && userAge !== null && userAge >= 45 && userSex === 'female') {
      tags.push({ key: 'demo-match', label: 'Postmenopausal formulation', match: true });
    } else if (ls === 'prenatal' && userSex === 'female') {
      tags.push({ key: 'demo-info', label: 'Prenatal formulation', match: false });
    } else if (ls === 'youth_under_18' && userAge !== null && userAge < 18) {
      tags.push({ key: 'demo-match', label: 'For under 18', match: true });
    } else if (ls && ls !== 'general') {
      // Life-stage tagged but doesn't match user demographic — show as info, not match
      const map = {
        adults_50_plus: 'Adults 50+ formulation',
        men_50_plus: 'Men 50+ formulation',
        postmenopausal: 'Postmenopausal',
        prenatal: 'Prenatal',
        youth_under_18: 'Under 18',
      };
      if (map[ls]) tags.push({ key: 'demo-info', label: map[ls], match: false });
    }

    // Sex relevance — only meaningful when product is sex-specific
    if (sr === 'female' && userSex === 'female') {
      tags.push({ key: 'sex-match', label: 'Female-formulated', match: true });
    } else if (sr === 'male' && userSex === 'male') {
      tags.push({ key: 'sex-match', label: 'Male-formulated', match: true });
    } else if (sr === 'female' && userSex && userSex !== 'female') {
      // Sex-specific product, but user is different sex — flag as info
      tags.push({ key: 'sex-info', label: 'Female-formulated', match: false });
    } else if (sr === 'male' && userSex && userSex !== 'male') {
      tags.push({ key: 'sex-info', label: 'Male-formulated', match: false });
    }

    return tags;
  }

  function toRef(product, reason) {
    return {
      handle: product.handle,
      title: product.title,
      vendor: product.vendor,
      ingredient: extractIngredient(product.title || '') || '',
      image_url: product.image_url || '',
      vsf_score: product.score || 0,
      tier: product.tier || 'provisional',
      certifications: product.certifications || [],
      reason: reason || '',
    };
  }

  // ---------- Lifestyle & Biohacking Recommendations ----------
  function generateLifestyleRecommendations(goals, sex, age) {
    const recs = [];

    // Goal-specific lifestyle recommendations
    const lifestyleByGoal = {
      sleep_recovery: [
        { title: 'Optimize Sleep Environment', text: 'Keep bedroom 65-68°F, pitch-black with blackout curtains, and use white noise or earplugs. Blue light blocking glasses 2h before bed.', icon: '🌙' },
        { title: 'Sleep Consistency Matters', text: 'Same bedtime and wake time 7 days a week. This trains your circadian rhythm more effectively than any supplement.', icon: '⏰' },
        { title: 'Temperature Regulation', text: 'Hot bath or shower 90min before bed (body temperature drop signals sleep). Cooling mattress pad for deeper REM cycles.', icon: '🛁' },
      ],
      energy_vitality: [
        { title: 'Morning Sunlight Exposure', text: '10-15min of natural light within 30min of waking. Resets circadian rhythm and boosts cortisol at the right time for all-day energy.', icon: '☀️' },
        { title: 'Strategic Caffeine Timing', text: 'Delay caffeine 90-120min after waking (adenosine clears naturally). Last cup before 2pm to protect sleep quality.', icon: '☕' },
        { title: 'Movement Snacks', text: '2min movement breaks every hour. Jump squats, desk push-ups, or walking stairs spike energy better than sitting.', icon: '🏃' },
      ],
      focus_cognition: [
        { title: 'Deep Work Blocks', text: '90min focused sessions with 15min breaks. Brain operates in 90min ultradian rhythms. Match your work to biology.', icon: '🧠' },
        { title: 'Avoid Glucose Spikes', text: 'Protein + fat breakfast instead of carbs alone. Stable blood sugar = stable focus. Save carbs for post-workout.', icon: '🥑' },
        { title: 'Binaural Beats & Music', text: '40Hz gamma waves or classical music during deep work. Proven to enhance concentration and memory formation.', icon: '🎧' },
      ],
      reduce_inflammation: [
        { title: 'Anti-Inflammatory Diet', text: 'Eliminate seed oils, processed foods, excess sugar. Focus on wild fish, grass-fed meat, colorful vegetables, berries.', icon: '🥗' },
        { title: 'Cold Exposure', text: '3min cold shower or ice bath 2-3x/week. Reduces inflammatory markers (IL-6, CRP) and boosts immune function.', icon: '❄️' },
        { title: 'Stress Management', text: 'Chronic stress drives inflammation. Daily meditation, breathwork (box breathing 4-4-4-4), or yoga proven to lower cortisol.', icon: '🧘' },
      ],
      longevity_healthspan: [
        { title: 'Time-Restricted Eating', text: '16:8 or 18:6 fasting window. Activates autophagy (cellular cleanup), improves insulin sensitivity, promotes longevity pathways.', icon: '⏳' },
        { title: 'Zone 2 Cardio', text: '3-4 sessions/week at 60-70% max HR for 45min. Builds mitochondria, the powerhouse of longevity.', icon: '🚴' },
        { title: 'Social Connection', text: 'Strong relationships = #1 predictor of longevity (Harvard 80-year study). Prioritize quality time with loved ones.', icon: '❤️' },
      ],
      strength_muscle: [
        { title: 'Progressive Overload', text: 'Increase weight/reps/sets every 2 weeks. Muscle growth requires consistent progressive stimulus over time.', icon: '💪' },
        { title: 'Protein Timing', text: '30-40g protein within 2h post-workout. Leucine threshold triggers muscle protein synthesis. 1g/lb bodyweight daily minimum.', icon: '🥩' },
        { title: 'Sleep for Gains', text: '7-9h nightly. 80% of growth hormone released during deep sleep. Skimp on sleep = leave gains on the table.', icon: '😴' },
      ],
      athletic_recovery: [
        { title: 'Active Recovery Days', text: 'Light movement (yoga, swimming, walking) on rest days. Increases blood flow and clears metabolic waste faster than complete rest.', icon: '🚶' },
        { title: 'Contrast Therapy', text: '3min hot sauna + 1min cold plunge, 3 rounds. Accelerates muscle recovery and reduces DOMS by 30-50%.', icon: '🔥' },
        { title: 'Hydration Strategy', text: 'Drink 0.5-1oz per lb bodyweight daily. Add electrolytes if sweating heavily. Dehydration = impaired recovery + performance.', icon: '💧' },
      ],
      weight_loss: [
        { title: 'Calorie Deficit Fundamentals', text: '500-750 cal deficit for 1-1.5lb loss/week. Track protein (1g/lb) to preserve muscle. Patience over perfection.', icon: '📊' },
        { title: 'NEAT Optimization', text: 'Non-Exercise Activity Thermogenesis = 15-30% daily calories. Walk 10k steps, take stairs, stand while working. Small moves compound.', icon: '🚶' },
        { title: 'Sleep & Stress Control', text: 'Poor sleep increases ghrelin (hunger hormone) + cortisol (fat storage). 7-9h sleep non-negotiable for fat loss.', icon: '😌' },
      ],
    };

    // Add goal-specific recommendations
    goals.forEach(goal => {
      if (lifestyleByGoal[goal]) {
        recs.push(...lifestyleByGoal[goal]);
      }
    });

    // Age-specific recommendations
    if (age >= 50) {
      recs.push({ 
        title: 'Resistance Training is Essential', 
        text: 'After 50, you lose 1-2% muscle mass yearly without resistance training. Lift weights 3x/week to maintain strength, bone density, and metabolic health.', 
        icon: '🏋️' 
      });
      recs.push({ 
        title: 'Joint Mobility Work', 
        text: '10min daily mobility routine (hip openers, shoulder circles, spinal twists). Prevents stiffness and maintains range of motion.', 
        icon: '🤸' 
      });
    }

    if (age >= 30 && age < 50) {
      recs.push({ 
        title: 'Metabolic Health Baseline', 
        text: 'Get annual blood work: fasting glucose, HbA1c, lipid panel, vitamin D, inflammation markers. Catch issues early when they\'re reversible.', 
        icon: '🩺' 
      });
    }

    if (age >= 18 && age < 30) {
      recs.push({ 
        title: 'Build Healthy Habits Now', 
        text: 'Your 20s are when lifelong patterns form. Prioritize sleep, movement, and real food. Future you will thank you.', 
        icon: '🌱' 
      });
    }

    // Sex-specific recommendations
    if (sex === 'female') {
      recs.push({ 
        title: 'Cycle-Synced Training', 
        text: 'Days 1-14 (follicular): higher intensity, heavier weights. Days 15-28 (luteal): lower intensity, focus on recovery and flexibility.', 
        icon: '🌸' 
      });
      recs.push({ 
        title: 'Iron Status Monitoring', 
        text: 'Women are 10x more likely to be iron deficient. Get ferritin tested annually. Low iron = fatigue, brain fog, poor recovery.', 
        icon: '💉' 
      });
    }

    if (sex === 'male') {
      recs.push({ 
        title: 'Testosterone Optimization', 
        text: 'Heavy compound lifts (squats, deadlifts), 7-9h sleep, manage stress, healthy fats (olive oil, avocado, fatty fish). Avoid excessive alcohol.', 
        icon: '⚡' 
      });
    }

    // Universal recommendations if we have few specific ones
    if (recs.length < 4) {
      recs.push({ 
        title: 'Hydration Baseline', 
        text: 'Drink half your bodyweight (lbs) in oz daily. Add electrolytes if training hard. Proper hydration improves energy, focus, and recovery.', 
        icon: '💧' 
      });
      recs.push({ 
        title: 'Breath Work Practice', 
        text: '5min daily box breathing (4sec in, 4sec hold, 4sec out, 4sec hold). Activates parasympathetic nervous system, reduces stress.', 
        icon: '🌬️' 
      });
    }

    // Return top 6 most relevant
    return recs.slice(0, 6);
  }

  function generateBiohackingTips(goals, sex, age, score) {
    const tips = [];

    // Goal-specific biohacking strategies
    const biohacksByGoal = {
      sleep_recovery: [
        { title: 'Magnesium Glycinate + L-Theanine Stack', text: '400mg mag glycinate + 200mg L-theanine 60min before bed. Calms nervous system and improves sleep latency.', category: 'supplement' },
        { title: 'Red Light Therapy', text: '10min red/near-infrared light (660nm + 850nm) in evening. Boosts melatonin production naturally.', category: 'device' },
        { title: 'Mouth Taping', text: 'Medical-grade tape over closed lips. Forces nasal breathing = deeper sleep, better oxygen uptake, reduced snoring.', category: 'protocol' },
      ],
      energy_vitality: [
        { title: 'NAD+ Precursors', text: 'NMN (500mg) or NR (300mg) in morning. Boosts cellular energy production, declines with age.', category: 'supplement' },
        { title: 'Cold Shower Finishing', text: '30-60sec cold blast at end of shower. Spikes norepinephrine for instant alertness and mood boost.', category: 'protocol' },
        { title: 'Electrolyte Optimization', text: 'Add sea salt + potassium + magnesium to water first thing. Cellular hydration = instant energy upgrade.', category: 'nutrition' },
      ],
      focus_cognition: [
        { title: 'Lions Mane + Caffeine + L-Theanine', text: '500mg lions mane + 100mg caffeine + 200mg L-theanine. Synergistic nootropic stack for sustained focus without jitters.', category: 'supplement' },
        { title: 'Neurofeedback Training', text: 'Muse or other EEG headband. Train your brain to enter flow state on command with real-time feedback.', category: 'device' },
        { title: 'Pomodoro + Dopamine Fasting', text: '25min work blocks, eliminate all distractions. Retrain attention span. Social media/notifications = cognitive kryptonite.', category: 'protocol' },
      ],
      reduce_inflammation: [
        { title: 'Curcumin + Piperine Stack', text: '1000mg curcumin phytosome + 20mg black pepper extract. Absorption is key; piperine increases bioavailability 2000%.', category: 'supplement' },
        { title: 'Grounding (Earthing)', text: '30min barefoot on grass or sand daily. Transfers electrons from earth, reduces inflammation markers measurably.', category: 'protocol' },
        { title: 'Omega-3 Loading', text: '2-3g EPA+DHA daily from fish oil or algae. Gold standard anti-inflammatory. Get triglyceride form for best absorption.', category: 'supplement' },
      ],
      longevity_healthspan: [
        { title: 'David Sinclair Stack', text: 'NMN 500mg + Resveratrol 500mg + TMG 500mg. Activates sirtuins, NAD+ production, and methylation pathways.', category: 'supplement' },
        { title: 'VO2 Max Training', text: '4min intervals at 90-95% max HR, 1x/week. VO2 max = strongest predictor of all-cause mortality. Trainable at any age.', category: 'protocol' },
        { title: 'Continuous Glucose Monitor', text: 'Freestyle Libre or Dexcom. Learn which foods spike your blood sugar. Metabolic health = longevity foundation.', category: 'device' },
      ],
      strength_muscle: [
        { title: 'Creatine Monohydrate', text: '5g daily, every day. The most researched supplement ever. +5-10% strength gains, muscle fullness, cognitive benefits.', category: 'supplement' },
        { title: 'Blood Flow Restriction (BFR)', text: 'Occlusion bands at 50-70% arterial pressure. Build muscle with 30% of normal weight. Joint-friendly gains.', category: 'protocol' },
        { title: 'HMB for Muscle Preservation', text: '3g HMB during cuts or over 40. Prevents muscle breakdown, especially in calorie deficits or with aging.', category: 'supplement' },
      ],
      athletic_recovery: [
        { title: 'Tart Cherry Juice', text: '8oz post-workout. Reduces muscle soreness by 24-48h, natural anti-inflammatory, improves sleep quality.', category: 'nutrition' },
        { title: 'Percussion Therapy', text: 'Theragun or Hypervolt 2min per muscle group. Breaks up adhesions, increases blood flow, accelerates recovery.', category: 'device' },
        { title: 'BCAA + Electrolytes Intra-Workout', text: '10g BCAAs + sodium/potassium during training. Prevents muscle breakdown, maintains hydration, sustains performance.', category: 'supplement' },
      ],
      weight_loss: [
        { title: 'Berberine or Metformin', text: '500mg berberine 3x daily with meals. Improves insulin sensitivity, helps partition nutrients away from fat storage.', category: 'supplement' },
        { title: 'Continuous Glucose Monitor Hack', text: 'Wear CGM for 2 weeks. Identify your personal trigger foods. Eliminate glucose spikes = accelerated fat loss.', category: 'device' },
        { title: 'Protein Leverage Hypothesis', text: 'Hit 1g protein per lb daily FIRST. Natural appetite suppression. High protein = harder to overeat calories.', category: 'nutrition' },
      ],
    };

    // Add goal-specific biohacking tips
    goals.forEach(goal => {
      if (biohacksByGoal[goal]) {
        tips.push(...biohacksByGoal[goal]);
      }
    });

    // Score-specific recommendations
    if (score < 50) {
      tips.push({ 
        title: 'Start with Sleep Audit', 
        text: 'Track sleep with Oura Ring or Whoop for 2 weeks. You can\'t optimize what you don\'t measure. Sleep is foundation-level.', 
        category: 'device' 
      });
      tips.push({ 
        title: 'Eliminate Before You Optimize', 
        text: 'Cut alcohol, reduce processed foods, fix sleep. Master the basics before adding fancy supplements or protocols.', 
        category: 'mindset' 
      });
    }

    if (score >= 70) {
      tips.push({ 
        title: 'Advanced: Peptides', 
        text: 'Consider BPC-157 for injury recovery, or CJC-1295/Ipamorelin for GH optimization. Consult sports medicine physician.', 
        category: 'advanced' 
      });
      tips.push({ 
        title: 'Track HRV Daily', 
        text: 'Heart Rate Variability = nervous system resilience. Use Oura or Whoop. Train hard when HRV is high, rest when low.', 
        category: 'device' 
      });
    }

    // Age-specific biohacking
    if (age >= 45) {
      tips.push({ 
        title: 'NAD+ IV Therapy', 
        text: 'Quarterly NAD+ infusions (500-1000mg). Cellular energy, DNA repair, mitochondrial function. Expensive but transformative.', 
        category: 'advanced' 
      });
      tips.push({ 
        title: 'Hormone Optimization', 
        text: 'Get full hormone panel (testosterone, DHEA, thyroid, cortisol). Work with longevity doctor to optimize levels to 25-35yr range.', 
        category: 'testing' 
      });
    }

    // Universal advanced tips if we have few
    if (tips.length < 4) {
      tips.push({ 
        title: 'Sauna Protocol', 
        text: '20min at 175-195°F, 4x/week. Increases heat shock proteins, cardiovascular benefits equal to moderate cardio, longevity signal.', 
        category: 'protocol' 
      });
      tips.push({ 
        title: 'Intermittent Fasting 16:8', 
        text: 'Eating window 12pm-8pm. Autophagy activation, insulin sensitivity, mental clarity. Start with 12:12 and progress.', 
        category: 'nutrition' 
      });
    }

    // Return top 5 most relevant
    return tips.slice(0, 5);
  }

  // ============== Public entry point ==============

  function vsfScore(input, catalog) {
    if (!input) input = {};
    if (!catalog || !catalog.products) catalog = { products: [] };

    const goals = (input.goals || []).slice(0, 3);
    const subcategoryTicks = (input.subcategory_ticks || []).filter(s => typeof s === 'string' && s.length);
    const symptoms = (input.symptoms || []).filter(s => typeof s === 'string' && SYMPTOM_TO_SUBCATEGORIES[s]);
    const ingredientTicks = normalizeIngredientTicks(input.ingredient_ticks); // Phase A.2
    const ticked = new Set(subcategoryTicks);

    // Sex filter: only apply when explicitly male or female. If the user
    // skipped the question, sexFilter stays null and no filtering occurs.
    const sexFilter = (input.sex === 'male' || input.sex === 'female') ? input.sex : null;
    const age = (typeof input.age === 'number') ? input.age : null;

    const matched = matchStack(subcategoryTicks, ingredientTicks, catalog.products, sexFilter, age);
    const subcategoryMissing = computeMissing(goals, symptoms, ticked, ingredientTicks, catalog.products, sexFilter, age);
    const ingredientMissing = computeMissingIngredients(goals, symptoms, ticked, ingredientTicks, catalog.products, sexFilter, age, 4);
    // Merge ingredient gaps in first (more specific), then subcategory gaps as backfill.
    const missingRecs = ingredientMissing.concat(subcategoryMissing);
    const whatElseRaw = computeWhatElseToConsider(goals, catalog.products, sexFilter, age);
    const scoreData = calculateScore(matched, input);

    // "Top picks in your categories" = catalog top-scored product in each
    // ticked subcategory. Reference information, not the user's stack.
    // Phase B: include relevance_tags so cards can surface life_stage / sex
    // matches that the engine applies silently today.
    const working = matched
      .filter(m => m.product)
      .map(m => Object.assign(
        toRef(m.product, reasonForCurrent(m)),
        {
          subcategory_key: m.subcategoryKey,
          relevance_tags: relevanceTagsFor(m.product, input),
        }
      ));

    const missing = missingRecs
      .map((rec, i) => Object.assign(
        toRef(rec.product, reasonForRecommendation(rec.product, rec.goalReason, i, rec.symptomReason, rec.ingredientDef)),
        {
          subcategory_key: rec.subcategoryKey,
          goal_reason: rec.goalReason,
          symptom_reason: rec.symptomReason,
          ingredient_reason: rec.ingredientDef ? { key: rec.ingredientDef.key, label: rec.ingredientDef.label } : null,
          relevance_tags: relevanceTagsFor(rec.product, input),
        }
      ))
      .slice(0, 6);

    // What else to consider: per non-supp category, project products to refs
    const whatElseToConsider = {};
    for (const catKey of Object.keys(whatElseRaw)) {
      whatElseToConsider[catKey] = whatElseRaw[catKey].map(p => Object.assign(
        toRef(p, ''),
        {
          vsf_category: catKey,
          vsf_subcategory: productSubcategories(p),
          relevance_tags: relevanceTagsFor(p, input),
        }
      ));
    }

    const lifestyleRecs = generateLifestyleRecommendations(goals, sexFilter, age);
    const biohackingTips = generateBiohackingTips(goals, sexFilter, age, scoreData.total);
    const improvementTips = generateTips(scoreData, input, missing); // Phase A.3

    return {
      score: scoreData.total,
      tier_label: scoreToTier(scoreData.total),
      verdict: generateVerdict(scoreData, matched, input, missing, whatElseToConsider),
      improvement_tips: improvementTips,
      goals: goals.map(g => GOAL_DISPLAY[g] || g),
      working: working,
      missing: missing,
      whatElseToConsider: whatElseToConsider,
      lifestyle: lifestyleRecs,
      biohacking: biohackingTips,
      meta: {
        version: 2,
        engine: 'deterministic',
        score_components: scoreData.components,
        n_ticked: scoreData.n_ticked,
        n_matched: scoreData.n_matched,
        n_recommended: missing.length,
      },
    };
  }

  // ==========================================================================
  // findProtocol (D16, 2026-06-21) — Find Your Protocol output engine.
  //
  // Replaces vsfScore for the new "Find Your Protocol" flow. Same catalog,
  // simpler input ({ goals, symptoms }), and a structured output suitable for
  // the new protocol-table results page.
  //
  // The score gauge / verdict / what's-working / what's-missing / what-else
  // are all gone. Instead we emit two flat lists:
  //   - basics      → core protocol (pre-checked on the results page)
  //   - biohackers  → extended protocol (unchecked, scrollable below)
  //
  // Each entry: { priority, vsf_category, vsf_subcategory, product }.
  // The results page renders these as table rows; the comparison view re-uses
  // the catalog filter to surface ranked alternatives.
  // ==========================================================================

  // SYMPTOM_TO_SUBCATEGORIES already declared above (line ~62); reused here.
  // Per-subcategory cap of products to surface in the protocol (across both
  // basics + biohackers). Keeps the protocol concise.
  const MAX_PRODUCTS_PER_SUBCATEGORY = 1;

  // Tier ordering for sort — Elite > Verified > Standard > everything else.
  const TIER_RANK = { elite: 4, verified: 3, standard: 2, provisional: 1, rejected: 0 };

  function tierRank(p) {
    const t = String(p.tier || '').toLowerCase();
    return TIER_RANK[t] != null ? TIER_RANK[t] : 0;
  }

  // ==========================================================================
  // 2026-06-22 — Per-subcategory rationale used to build the "Why this pick"
  // line on each Protocol row. One concise sentence per subcategory explaining
  // why it matters for biohacking (independent of which goal triggered it —
  // the combiner adds goal context separately).
  // ==========================================================================
  const SUBCATEGORY_RATIONALE = {
    // Supplements
    vitamins_micronutrients:    'Foundational daily multi or single nutrient for closing common gaps the standard diet misses.',
    protein_amino_acids:        'Protein adequacy is the single most underrated lever for body composition, recovery, and satiety.',
    gut_digestive:              'Gut barrier integrity and microbiome diversity gate almost every other system — sleep, mood, immune, inflammation.',
    immune_antioxidants:        'Oxidative stress is one of the most consistent biomarkers across chronic disease — antioxidants help offset day-to-day load.',
    cognitive_nootropics:       'A small set of nootropics have real RCT data — focus, memory consolidation, and stress resilience.',
    energy_metabolic:           'Cellular energy production (mitochondrial cofactors, B-vitamins) often shows up as "tired but wired" before lab work catches it.',
    hormonal_endocrine:         'Adrenals, thyroid, and sex hormones interact — supporting one usually requires checking the others.',
    joint_bone_connective:      'Collagen, vitamin K2, and joint support stack particularly well past your mid-30s when collagen synthesis drops.',
    cardiovascular_heart:       'Cardiovascular markers respond fastest to lifestyle + targeted supplementation — magnesium, omega-3, CoQ10.',
    longevity_cellular:         'NAD+ precursors, polyphenols, and senolytic-adjacent nutrients have promising early data, though long-term human trials are still small.',
    weight_metabolic_health:    'Metabolic flexibility (the ability to switch between glucose and fat oxidation) is the underlying biomarker most people are actually targeting.',
    sleep_supplements_nutraceuticals: 'Magnesium glycinate, l-theanine, and low-dose melatonin are the three with the most consistent sleep evidence — others are mostly placebo.',
    stress_mood_mental:         'Magnesium, adaptogens, and amino-acid precursors (glycine, taurine) are the supplements with consistent stress/mood RCT data.',
    skin_hair_beauty:           'Collagen peptides, biotin, and antioxidants address the visible signs of slower cellular turnover.',
    womens_health:              'Cycle-aware and life-stage-specific formulations matter — most off-the-shelf options ignore phase variability.',
    mens_health:                'Targeted formulations around testosterone, prostate, and reproductive function — though most claims here outrun the evidence.',

    // Wearables & Tracking
    smartwatches:               'A smartwatch with FDA-cleared health features gives you continuous HR, sleep staging, and (on some models) ECG without strapping a new device on every day.',
    fitness_activity_trackers:  'Step counts and active minutes give you a baseline you can change. The best ones add accurate HR and sleep tracking too.',
    cgm:                        'Continuous glucose monitoring exposes the food → energy patterns you absolutely cannot see from morning fatigue or one-off lab draws.',
    smart_rings:                'Smart rings track HRV, sleep, and temperature with less day-to-day friction than a watch — best for people who already wear a separate watch.',
    cardiac_ecg:                'FDA-cleared ECG wearables catch arrhythmias (especially AFib) earlier than annual physicals do — important if you have risk factors.',
    spo2_respiratory:           'Pulse oximetry tracks blood oxygen and respiration — useful for sleep apnea screening and altitude/respiratory issues.',
    blood_pressure:             'At-home BP monitoring is the AHA-recommended way to diagnose hypertension. Single in-office readings miss white-coat and masked hypertension.',
    biometric_patches:          'Continuous multi-parameter patches give you weeks of HR, temp, and respiration without a wearable — useful for trend analysis.',
    smart_clothing:             'Sensor garments measure muscle activity and posture during training — niche but useful for athletes and rehab.',
    hearables:                  'In-ear sensors capture body temp and HR with less motion artifact than wrist devices.',

    // Diagnostics & Testing
    blood_panel_metabolic:      'At-home blood panels reveal lipids, glucose, inflammation, and metabolic markers that morning energy alone cannot.',
    glucose_diabetes_monitoring:'Real-time glucose data turns "I think this food crashes me" into measurable proof.',
    genetic_dna:                'DNA tests identify polygenic risk factors and pharmacogenomic responses — most useful when paired with active lifestyle changes.',
    gut_microbiome_testing:     'Stool-based microbiome panels give you a baseline diversity score and identify specific gut imbalances.',
    hormone_fertility_testing:  'At-home hormone panels surface cortisol, thyroid, sex-hormone patterns — useful for fatigue, mood, and reproductive optimization.',
    nutrient_deficiency_testing:'Vitamin D, B12, magnesium, and iron deficiencies are widespread and reversible — the testing pays for itself when you find one.',
    food_sensitivity_allergy:   'IgG and IgE panels identify food responses that aren’t obvious from food journaling alone.',
    sti_infectious_disease:     'At-home testing for sexually transmitted and infectious diseases — cheaper, faster, and more private than clinic visits.',
    cancer_chronic_screening:   'Early-detection panels for colorectal, cervical, and chronic disease markers — worth doing periodically past 40.',
    heavy_metals_toxins:        'Heavy metals and environmental toxins accumulate silently — a one-time panel reveals exposure baselines.',

    // Sleep Optimization
    sleep_tracking_wearables:   'Wearable sleep staging is the cheapest objective measurement of your sleep architecture you can get without a sleep lab.',
    smart_mattresses:           'Smart mattresses with built-in temperature control, sensors, and zone adjustment do more for sleep than any tracker can.',
    sleep_apnea_cpap:           'Sleep apnea drives chronic fatigue, BP issues, and cognitive decline more than most people realize — CPAP transforms it when present.',
    noncontact_sleep_monitors:  'Under-mattress sensors give better sleep staging accuracy than wrist-based wearables without you wearing anything.',
    sleep_environment:          'Most people overspend on tracking and underspend on environment. The room temperature, darkness, and weight on your body do the heavy lifting.',
    white_noise_sound:          'Continuous broadband sound masks intermittent disturbances (snoring, traffic, neighbors) — pink and brown noise outperform white noise for most people.',
    neurostim_sleep_headbands:  'EEG-based headbands targeting slow-wave sleep are early-stage but the mechanism is plausible and the studies are growing.',
    cbti_digital_therapy:       'CBT-I apps deliver the gold-standard insomnia treatment at a fraction of the cost of a therapist.',
    sleep_pillows_positional:   'Pillow ergonomics and positional aids correct the cervical spine alignment most people get wrong without realizing.',

    // Recovery & Regeneration
    percussion_vibration:       'Percussion massage modestly reduces post-exercise soreness and improves short-term range of motion. Not magic — but real RCT data exists.',
    compression_therapy:        'Pneumatic compression boots accelerate lymphatic drainage and reduce post-exercise soreness — used by professional athletes for a reason.',
    cold_therapy:               'Cold exposure has strong data for mood, dopamine, and brown fat activation — and notably also blunts hypertrophy if done right after strength training.',
    red_light_photobiomodulation:'Red light therapy has good evidence for skin, modest evidence for joint pain and DOMS. Pick by wavelength, irradiance, and dose.',
    sauna_heat_therapy:         'Regular sauna use has the strongest all-cause mortality data of any single recovery modality (Laukkanen 2015) — dose-dependent.',
    ems_tens:                   'EMS and TENS deliver targeted muscle stimulation and pain modulation — well-validated for both performance and rehab.',
    hbot:                       'Hyperbaric oxygen has compelling but small-trial data for tissue repair and recovery — worth considering for specific injuries.',
    stretch_mobility:           'Mobility work is the cheapest, highest-leverage recovery intervention — most people skip it because no device is involved.',
    hrv_recovery_apps:          'HRV-driven training apps adjust your workout based on your nervous-system readiness — useful once you have data to act on.',
    contrast_therapy:           'Alternating heat and cold has emerging RCT data for recovery and circulation — combines benefits of sauna + cold plunge.',
  };

  function rationaleForSubcategory(subKey) {
    return SUBCATEGORY_RATIONALE[subKey] || 'Worth considering as part of a comprehensive protocol.';
  }

  function rationaleForProduct(product) {
    // Prefer the LLM-written explanation when present (all non-supps have one).
    if (product.vsf_explanation && product.vsf_explanation.trim()) {
      return product.vsf_explanation.trim();
    }
    // Supplement fallback by tier — generic but honest.
    const tier = String(product.tier || '').toLowerCase();
    const score = product.score || 0;
    if (tier === 'elite' || tier === 'verified') {
      return 'This product is one of the higher-rated options we surface for this category. Worth a closer look on the product page.';
    }
    if (tier === 'standard') {
      return 'A reasonable option in this category — cleared our Standard tier with decent documentation. Compare alternatives if budget allows.';
    }
    if (tier === 'provisional') {
      return 'Supplement scoring uses a different rubric than devices — most quality supplements score in the 30–60 range. Pick by brand transparency and form (e.g. glycinate over oxide for magnesium).';
    }
    return 'Surfacing this with full transparency on its rejection reason. Compare with alternatives before adding to your protocol.';
  }

  function buildReason(entry) {
    const subRationale = rationaleForSubcategory(entry.vsf_subcategory);
    const prodRationale = rationaleForProduct(entry.product);
    return subRationale + ' ' + prodRationale;
  }

  // ==========================================================================
  // 2026-06-29 — Per-row personalized "Why this?" bullets.
  //
  // Returns 5–7 short strings that explain why THIS product was picked for
  // THIS user's actual inputs. Each bullet ties back to a quiz answer (goal,
  // symptom, lifestyle slider, current-stack tick, demographic). The result
  // page shows them inside a collapsible panel behind a "Why this?" button.
  //
  // Ordering is roughly "most-specific-to-user first, generic last" so that
  // even after the 7-bullet cap the user sees the personalized hits.
  // ==========================================================================
  function buildReasonBullets(entry, input) {
    const bullets = [];
    const product = entry.product || {};
    const subKey = entry.vsf_subcategory;
    const goals = (input && input.goals) || [];
    const symptoms = (input && input.symptoms) || [];
    const habits = (input && input.habits) || null;
    const sex = (input && input.sex) || null;
    const age = (input && typeof input.age === 'number') ? input.age : null;
    const ticks = new Set((input && input.subcategory_ticks) || []);
    const diet = (input && input.diet_pattern) || null;
    const alcohol = (input && input.alcohol) || null;
    const bloodworkRecent = (input && input.bloodwork_recent);

    // 1. Goal match (use the first goal that maps to this subcategory)
    for (const g of goals) {
      if ((GOAL_TO_SUBCATEGORIES[g] || []).indexOf(subKey) !== -1) {
        bullets.push('Matches your goal: ' + (GOAL_DISPLAY[g] || g) + '.');
        break;
      }
    }

    // 2. Symptom match
    for (const s of symptoms) {
      if ((SYMPTOM_TO_SUBCATEGORIES[s] || []).indexOf(subKey) !== -1) {
        bullets.push('Addresses a concern you flagged: ' + (SYMPTOM_DISPLAY[s] || s) + '.');
        break;
      }
    }

    // 3. Lifestyle gap signals
    if (habits) {
      if (subKey.indexOf('sleep_') === 0 && typeof habits.sleep === 'number' && habits.sleep <= 5) {
        bullets.push('Your sleep quality (' + habits.sleep + '/10) is one of the highest-leverage areas in your inputs.');
      }
      if ((subKey === 'percussion_vibration' || subKey === 'stretch_mobility' ||
           subKey === 'compression_therapy' || subKey === 'ems_tens') &&
          typeof habits.exercise === 'number' && habits.exercise <= 5) {
        bullets.push('Builds recovery capacity to match your exercise level (' + habits.exercise + '/10).');
      }
      if ((subKey === 'vitamins_micronutrients' || subKey === 'protein_amino_acids' ||
           subKey === 'gut_digestive') &&
          typeof habits.diet === 'number' && habits.diet <= 5) {
        bullets.push('Helps close gaps your diet quality (' + habits.diet + '/10) is likely leaving on the table.');
      }
      if ((subKey === 'stress_mood_mental' || subKey === 'neurostim_sleep_headbands' ||
           subKey === 'hrv_recovery_apps' || subKey === 'cognitive_nootropics') &&
          typeof habits.stress === 'number' && habits.stress >= 6) {
        bullets.push('Targeted at your reported stress (' + habits.stress + '/10).');
      }
    }

    // 4. Diet pattern relevance
    if (diet === 'vegan' && (subKey === 'vitamins_micronutrients' || subKey === 'protein_amino_acids')) {
      bullets.push('Plant-only diets are most often short on what this category covers (B12, iron, omega-3, complete protein).');
    } else if (diet === 'vegetarian' && (subKey === 'vitamins_micronutrients' || subKey === 'protein_amino_acids')) {
      bullets.push('Vegetarian patterns commonly need extra support in this category (B12, iron, zinc).');
    } else if (diet === 'keto' && (subKey === 'vitamins_micronutrients' || subKey === 'energy_metabolic')) {
      bullets.push('Keto patterns drive electrolyte loss — magnesium, sodium and potassium become critical.');
    } else if (diet === 'carnivore' && subKey === 'gut_digestive') {
      bullets.push('Carnivore patterns often lack fiber-driven gut support — this fills the gap.');
    }

    // 5. Alcohol
    if ((alcohol === 'regular' || alcohol === 'daily') &&
        (subKey === 'immune_antioxidants' || subKey === 'gut_digestive' ||
         subKey === 'vitamins_micronutrients' || subKey === 'energy_metabolic')) {
      bullets.push('Regular alcohol depletes the nutrients in this category — worth restoring.');
    }

    // 6. Bloodwork-driven diagnostics
    if (bloodworkRecent === false && entry.vsf_category === 'diagnostics_testing') {
      bullets.push('You haven\'t had bloodwork in the last 12 months — this gets you the baseline to actually optimize from.');
    }

    // 7. Demographic / life-stage signal
    if (age !== null && product.life_stage === 'adults_50_plus' && age >= 50) {
      bullets.push('Formulated for adults 50+ — matches your inputs (age ' + age + ').');
    } else if (age !== null && product.life_stage === 'men_50_plus' && age >= 50 && sex === 'male') {
      bullets.push('Formulated for men 50+ — matches your inputs.');
    } else if (age !== null && product.life_stage === 'postmenopausal' && age >= 45 && sex === 'female') {
      bullets.push('Postmenopausal formulation — matches your life-stage.');
    } else if (age !== null && age >= 50) {
      const tags = ageBandTags(age);
      if (tags.indexOf('joint') !== -1 && subKey === 'joint_bone_connective') {
        bullets.push('Joint health rises in priority in your age band (50+).');
      } else if (tags.indexOf('cardiovascular') !== -1 && subKey === 'cardiovascular_heart') {
        bullets.push('Cardiovascular optimization is flagged for your age band.');
      } else if (tags.indexOf('longevity') !== -1 && subKey === 'longevity_cellular') {
        bullets.push('Longevity-supportive — relevant for your age band.');
      }
    }
    if (sex === 'female' && product.sex_relevance === 'female') {
      bullets.push('Formulated for female physiology.');
    } else if (sex === 'male' && product.sex_relevance === 'male') {
      bullets.push('Formulated for male physiology.');
    }

    // 8. Stack-vs-gap framing
    if (ticks.has(subKey)) {
      bullets.push('You mentioned taking this category — this is the highest-scored option in our catalog for it, so worth comparing against what you currently use.');
    } else if (ticks.size > 0 && entry.vsf_category === 'supplements') {
      bullets.push('Not in your current stack — closes a category gap rather than duplicating what you already take.');
    }

    // 9. Subcategory rationale (the "why this category matters" sentence)
    bullets.push(rationaleForSubcategory(subKey));

    // 10. Product-level evidence — pull the first sentence from vsf_explanation
    // when we have one (all non-supps + some supps carry it). Splitting on
    // [.!?] followed by whitespace avoids breaking on decimals like "96.3%".
    if (product.vsf_explanation && product.vsf_explanation.trim()) {
      const raw = product.vsf_explanation.trim();
      const sentences = raw.split(/(?<=[.!?])\s+/);
      let snippet = (sentences[0] || raw).trim();
      if (!/[.!?]$/.test(snippet)) snippet += '.';
      if (snippet.length > 200) snippet = snippet.slice(0, 197) + '…';
      bullets.push(snippet);
    }

    // 11. Product strength evidence (Vyvata tier)
    const score = product.score || 0;
    if (score >= 90) {
      bullets.push('Vyvata Elite (' + score + '/100) — top of our catalog in this category.');
    } else if (score >= 75) {
      bullets.push('Vyvata Verified (' + score + '/100) — among the highest-scored options we surface.');
    } else if (score >= 60) {
      bullets.push('Cleared the Vyvata Standard tier (' + score + '/100).');
    } else if (score >= 50) {
      bullets.push('Floor-pass at ' + score + '/100 — surfaced for transparency; compare alternatives before committing.');
    }

    // De-dupe + cap at 7.
    const seen = new Set();
    const out = [];
    for (const b of bullets) {
      if (seen.has(b)) continue;
      seen.add(b);
      out.push(b);
      if (out.length >= 7) break;
    }
    return out;
  }

  function findProtocol(input, catalog) {
    const goals = (input && input.goals) || [];
    const symptoms = (input && input.symptoms) || [];
    const products = (catalog && catalog.products) || [];
    const ticks = new Set((input && input.subcategory_ticks) || []);
    const habits = (input && input.habits) || null;
    const sex = (input && input.sex) || null;
    const age = (input && typeof input.age === 'number') ? input.age : null;
    const diet = (input && input.diet_pattern) || null;
    const alcohol = (input && input.alcohol) || null;
    const bloodworkRecent = (input && input.bloodwork_recent);

    if (!goals.length) {
      return { basics: [], biohackers: [], goals: [] };
    }

    // ---------- 1. Subcategory weighting ----------
    // Base goal weight: earlier position in GOAL_TO_SUBCATEGORIES = higher weight.
    const subcategoryWeights = {};
    goals.forEach(g => {
      (GOAL_TO_SUBCATEGORIES[g] || []).forEach((sub, idx) => {
        subcategoryWeights[sub] = (subcategoryWeights[sub] || 0) + Math.max(1, 12 - idx);
      });
    });
    // Symptom emphasis
    symptoms.forEach(s => {
      (SYMPTOM_TO_SUBCATEGORIES[s] || []).forEach((sub, idx) => {
        subcategoryWeights[sub] = (subcategoryWeights[sub] || 0) + Math.max(1, 6 - idx);
      });
    });

    // ---------- 2. Lifestyle boosts ----------
    // The harder the user is struggling in an area, the bigger the boost to
    // subcategories that address it. Subcategories with no addressable
    // signal from lifestyle inputs are unaffected.
    if (habits) {
      if (typeof habits.sleep === 'number' && habits.sleep <= 5) {
        const boost = habits.sleep <= 3 ? 10 : 6;
        ['sleep_supplements_nutraceuticals', 'sleep_tracking_wearables', 'sleep_environment',
         'noncontact_sleep_monitors', 'smart_mattresses', 'sleep_pillows_positional',
         'white_noise_sound', 'stress_mood_mental', 'neurostim_sleep_headbands',
         'cbti_digital_therapy'].forEach(sk => {
          subcategoryWeights[sk] = (subcategoryWeights[sk] || 0) + boost;
        });
      }
      if (typeof habits.exercise === 'number' && habits.exercise <= 5) {
        const boost = habits.exercise <= 3 ? 8 : 5;
        ['percussion_vibration', 'stretch_mobility', 'compression_therapy', 'ems_tens',
         'cold_therapy', 'sauna_heat_therapy', 'red_light_photobiomodulation',
         'protein_amino_acids', 'energy_metabolic'].forEach(sk => {
          subcategoryWeights[sk] = (subcategoryWeights[sk] || 0) + boost;
        });
      }
      if (typeof habits.diet === 'number' && habits.diet <= 5) {
        const boost = habits.diet <= 3 ? 8 : 5;
        ['vitamins_micronutrients', 'protein_amino_acids', 'gut_digestive',
         'cardiovascular_heart', 'energy_metabolic', 'cgm',
         'blood_panel_metabolic', 'nutrient_deficiency_testing'].forEach(sk => {
          subcategoryWeights[sk] = (subcategoryWeights[sk] || 0) + boost;
        });
      }
      if (typeof habits.stress === 'number' && habits.stress >= 6) {
        const boost = habits.stress >= 8 ? 10 : 6;
        ['stress_mood_mental', 'sleep_supplements_nutraceuticals', 'cognitive_nootropics',
         'hrv_recovery_apps', 'neurostim_sleep_headbands', 'sauna_heat_therapy',
         'red_light_photobiomodulation', 'cbti_digital_therapy'].forEach(sk => {
          subcategoryWeights[sk] = (subcategoryWeights[sk] || 0) + boost;
        });
      }
      if (typeof habits.meditation === 'number' && habits.meditation < 10) {
        ['stress_mood_mental', 'neurostim_sleep_headbands', 'hrv_recovery_apps',
         'cbti_digital_therapy'].forEach(sk => {
          subcategoryWeights[sk] = (subcategoryWeights[sk] || 0) + 3;
        });
      }
    }

    // ---------- 3. Diet pattern ----------
    if (diet === 'vegan' || diet === 'vegetarian') {
      const boost = diet === 'vegan' ? 6 : 4;
      ['vitamins_micronutrients', 'protein_amino_acids'].forEach(sk => {
        subcategoryWeights[sk] = (subcategoryWeights[sk] || 0) + boost;
      });
    } else if (diet === 'keto') {
      ['vitamins_micronutrients', 'energy_metabolic'].forEach(sk => {
        subcategoryWeights[sk] = (subcategoryWeights[sk] || 0) + 4;
      });
    } else if (diet === 'carnivore') {
      ['gut_digestive', 'vitamins_micronutrients'].forEach(sk => {
        subcategoryWeights[sk] = (subcategoryWeights[sk] || 0) + 4;
      });
    }

    // ---------- 4. Alcohol ----------
    if (alcohol === 'regular' || alcohol === 'daily') {
      const boost = alcohol === 'daily' ? 7 : 4;
      ['immune_antioxidants', 'gut_digestive', 'vitamins_micronutrients', 'energy_metabolic']
        .forEach(sk => { subcategoryWeights[sk] = (subcategoryWeights[sk] || 0) + boost; });
    }

    // ---------- 5. Bloodwork ----------
    // If user hasn't tested, push diagnostics. Strong boost — bloodwork is
    // the upstream lever for most other recommendations.
    if (bloodworkRecent === false) {
      ['blood_panel_metabolic', 'nutrient_deficiency_testing', 'hormone_fertility_testing',
       'genetic_dna', 'gut_microbiome_testing'].forEach(sk => {
        subcategoryWeights[sk] = (subcategoryWeights[sk] || 0) + 7;
      });
    }

    // ---------- 6. Boost current-stack subcategories ----------
    // 2026-06-30 — INVERTED from the previous session's logic. The intent now
    // is: for categories the user already takes, surface the HIGHEST-rated
    // product in our catalog so they see the top upgrade candidate. Gaps
    // (goal-relevant subcategories the user did NOT tick) still get surfaced
    // by the base goal/symptom weights above, so the protocol covers both:
    // "here's the best in what you already take" + "here's what you're
    // missing." Down-stream, buildReasonBullets phrases each pick differently
    // depending on whether its subcategory is in `ticks`.
    ticks.forEach(sk => {
      subcategoryWeights[sk] = (subcategoryWeights[sk] || 0) + 15;
    });

    // ---------- 7. Demographic filtering on products ----------
    function passesDemographic(p) {
      if (sex === 'male' || sex === 'female') {
        if (p.sex_relevance && p.sex_relevance !== 'unisex' &&
            p.sex_relevance !== 'neutral' && p.sex_relevance !== sex) return false;
      }
      if (!lifeStageAllowed(p, sex, age)) return false;
      return true;
    }

    // ---------- 8. Top product per subcategory ----------
    const SCORE_FLOOR = 50;
    const subcatRecommendation = {};
    Object.keys(subcategoryWeights).forEach(subKey => {
      const matches = products.filter(p =>
        Array.isArray(p.vsf_subcategory) &&
        p.vsf_subcategory.indexOf(subKey) !== -1 &&
        (p.score || 0) >= SCORE_FLOOR &&
        passesDemographic(p)
      );
      if (!matches.length) return;
      matches.sort((a, b) => (b.score || 0) - (a.score || 0) || tierRank(b) - tierRank(a));
      subcatRecommendation[subKey] = matches.slice(0, MAX_PRODUCTS_PER_SUBCATEGORY);
    });

    // ---------- 9. Compose flat entries, ranked ----------
    const entries = [];
    Object.keys(subcatRecommendation).forEach(subKey => {
      subcatRecommendation[subKey].forEach(product => {
        const composite = subcategoryWeights[subKey] * ((product.score || 0) / 100);
        entries.push({ subKey, weight: subcategoryWeights[subKey], product, composite });
      });
    });
    entries.sort((a, b) => b.composite - a.composite);

    // ---------- 10. Split Basics vs Biohackers, diversify Basics by category ----------
    const BASICS_TARGET = 8;
    const BIOHACKERS_CAP = 24;
    const basics = [];
    const biohackers = [];
    const categoriesUsedInBasics = {};
    const pendingEntries = entries.slice();
    while (basics.length < BASICS_TARGET && pendingEntries.length) {
      let pickedIdx = -1;
      for (let i = 0; i < pendingEntries.length; i++) {
        const cat = pendingEntries[i].product.vsf_category;
        if (!categoriesUsedInBasics[cat]) { pickedIdx = i; break; }
      }
      if (pickedIdx === -1) pickedIdx = 0;
      const picked = pendingEntries.splice(pickedIdx, 1)[0];
      const entry = {
        priority: 'Basics',
        vsf_category: picked.product.vsf_category,
        vsf_subcategory: picked.subKey,
        product: picked.product,
      };
      entry.reason = buildReason(entry);
      entry.reason_bullets = buildReasonBullets(entry, input);
      basics.push(entry);
      categoriesUsedInBasics[picked.product.vsf_category] = true;
    }
    pendingEntries.slice(0, BIOHACKERS_CAP).forEach(e => {
      const entry = {
        priority: 'Biohackers',
        vsf_category: e.product.vsf_category,
        vsf_subcategory: e.subKey,
        product: e.product,
      };
      entry.reason = buildReason(entry);
      entry.reason_bullets = buildReasonBullets(entry, input);
      biohackers.push(entry);
    });

    return {
      basics: basics,
      biohackers: biohackers,
      goals: goals.map(g => GOAL_DISPLAY[g] || g),
      meta: {
        version: 2,
        engine: 'find-protocol',
        n_basics: basics.length,
        n_biohackers: biohackers.length,
        score_floor: SCORE_FLOOR,
        personalized_with: {
          sex: !!sex,
          age: age !== null,
          habits: !!habits,
          diet: !!diet,
          alcohol: !!alcohol,
          bloodwork: bloodworkRecent !== null && bloodworkRecent !== undefined,
          stack_dedup: ticks.size,
        },
      },
    };
  }

  // Expose globally so non-module scripts can use.
  window.VSF_ENGINE = {
    vsfScore: vsfScore,
    findProtocol: findProtocol,
    GOAL_DISPLAY: GOAL_DISPLAY,
    GOAL_TO_TAGS: GOAL_TO_TAGS,
    GOAL_TO_SUBCATEGORIES: GOAL_TO_SUBCATEGORIES,
    CATEGORY_KEYS: CATEGORY_KEYS,
    SCORE_WEIGHTS: SCORE_WEIGHTS,
  };
})();
